/* ============================================================
   Tabio AI Fit 診断チャット
   デザイン: Figma「Tabio_AI_UIDesign_ForEngineer」準拠
     TOP(相談) → 会話(コンシェルジュのローディング) → 結果(リプ＋カルーセル)
   API 契約: /api/diagnosis/{config,next,recommend}（fitDiagnosis.ts）
     GET  {api_base}/api/diagnosis/config
          -> { initialChips:[{label,keyword}], resultCount, maxQuestions }
     POST {api_base}/api/diagnosis/next     body { messages:[{role,content}] }
          -> { done, question, choices:[{label,keyword}], isNewTopic? }
             ※ choices は画面に出さない（追加質問はすべて自由入力で答える・先方依頼 2026-10-16）
     POST {api_base}/api/diagnosis/recommend body { messages:[{role,content}], newTopic? }
          -> { products:[{title,handle,price,imageUrl,productUrl,recommendation,...}], message }
             customerSizeCm?（推薦に使ったお客様のサイズ cm。無い・null なら会話から決める）
   messages 制約: role in {user,assistant} / content は非空・最大500字。
   customerId は任意。gid://shopify/Customer/{id} 形式のときのみ送る。
   ★「話題が切り替わったか」の判定は /next と /recommend の2か所にあるが、
     /next が true と判定した回だけ、同じターンの /recommend に newTopic として
     引き継ぐ（false は引き継がず、フィールド自体を送らない）。
     /next は判定結果を使って聞き返す（done=false）ので、判定は /next の時点で必ず要る。
     一方 /recommend は done=true の回にしか呼ばれないため、判定を /recommend だけに
     寄せることはできない。ただし実データ計測では /recommend の方が的中率が高いので、
     false は引き継がず /recommend 自身の判定に任せる
     （理由と実測値は runRecommend のコメント）。
   ★ うまくいかなかったときの挙動（片方が失敗したとき）
     ・/next が isNewTopic を返さない（判定していない回・古いサーバー）／false を返す
         → newTopic を送らない。/recommend は従来どおり自分で判定する。
     ・/next の通信そのものが失敗
         → 既存の catch でエラーを出して終わり。/recommend は呼ばない（商品は出ない）。
           話題の開始位置も動かないので、次のターンは元の状態から続けられる。
     ・/recommend が newTopic を解釈できない（古いサーバー）
         → 未知のフィールドとして無視される。従来どおりの推薦になる。
     詳細は state.topicStart / runRecommend のコメント。
   ============================================================ */
(function () {
  'use strict';

  if (window.__tabioAifitLoaded) return;
  window.__tabioAifitLoaded = true;

  var MAX_CONTENT = 500; // parseChatMessages の MAX_MESSAGE_LENGTH に合わせる
  // マスコットのコマ送り（2026-09-08 の支給素材で3コマ化）。
  // ★ 速いとチカチカし、遅いと止まって見える。3コマで 1 周 1.2 秒になる値。
  //   速さを変えたいときはここだけ触ればよい。
  var FRAME_INTERVAL_MS = 400;
  // 探し終えた合図の絵を見せる長さ。これが済んでから結果を出す。
  var LOADING_END_HOLD_MS = 700;

  // 相談文の色/サイズキーワード ⇄ バリアント値のマッチ（PoC 流用）
  var COLOR_GROUPS = [
    ['赤', 'レッド', 'red', 'アカ', 'ワイン'],
    ['黒', 'ブラック', 'black', 'クロ'],
    ['白', 'ホワイト', 'white', 'サラシ', 'オフホワイト', 'キナリ', 'アイボリー'],
    ['紺', 'ネイビー', 'navy', 'コン'],
    ['青', 'ブルー', 'blue', 'サックス', 'ターコイズ', '水色'],
    ['グレー', '灰', 'gray', 'grey', 'チャコール', 'charcoal', 'モク'],
    ['茶', 'ブラウン', 'brown', 'モカ', 'キャメル'],
    ['ベージュ', '生成り', 'beige', 'グレージュ'],
    ['ピンク', 'pink'],
    ['紫', 'ラベンダー', 'パープル', 'purple', 'lavender'],
    ['緑', 'グリーン', 'green', 'ミドリ', 'キミドリ', 'ライム', 'カーキ'],
    ['黄', '黄色', 'イエロー', 'yellow', 'マスタード', 'レモン'],
    ['オレンジ', 'orange']
  ];

  // ── アイコン（すべて Figma 支給の SVG をそのまま使用） ──
  var CARET_SVG =
    '<svg class="tabio-aifit__select-caret" xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true">' +
    '<path d="M10.5 0.5L5.5 5.5L0.5 0.5" stroke="black" stroke-linecap="round"/></svg>';

  // ★ 同じアイコンを2か所で使う（商品カードの「カートに追加」＝オレンジ／
  //   ヘッダーのカート件数バッジ＝白）。色は currentColor にして
  //   CSS 側（.tabio-aifit__cart svg と .tabio-aifit__cart-count）で決める。
  //   色を直接書くと、片方に合わせるともう片方が背景に埋もれる。
  var BAG_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none" aria-hidden="true">' +
    '<path d="M0.600098 4.46167H12.9577V11.4128C12.9577 12.6926 11.9204 13.7299 10.6407 13.7299H2.91716C1.63748 13.7299 0.600098 12.6926 0.600098 11.4128V4.46167Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>' +
    '<path d="M3.68945 4.46174V3.68939C3.68945 1.98315 5.07263 0.599976 6.77886 0.599976C8.48507 0.599976 9.86828 1.98315 9.86828 3.68939V4.46174" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>' +
    '</svg>';

  var NEXT_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">' +
    '<circle cx="22.9702" cy="22.9702" r="22.9702" transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 45.9404 45.9403)" fill="#222222"/>' +
    '<path d="M16.8257 22.7977H27.6217" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M23.3721 28.5404L29.1146 22.7979L23.3721 17.0553" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var DOWN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">' +
    '<circle cx="22.9702" cy="22.9702" r="22.9702" transform="matrix(1 0 0 -1 0 45.9403)" fill="#222222"/>' +
    '<path d="M23.1426 16.8257L23.1426 27.6217" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M17.3999 23.3721L23.1424 29.1147L28.885 23.3721" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── ユーティリティ ──
  // セキュリティレビュー指摘6: 旧実装（createTextNode + innerHTML）は & < > しか
  // 変換せず、" ' が素通りしていた。結果は src="..." alt="..." 等の属性値にも
  // 使うため、引用符で属性を突き破って onerror 等を注入できた。
  // 文字列置換方式で & < > " ' の5文字を変換する。
  // ★ & を最初に変換すること（後に回すと &lt; の & まで再変換され二重エスケープになる）。
  // 表示への影響なし: 結果は全箇所 innerHTML に入れており、ブラウザが実体参照を戻して表示する。
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatPrice(price) {
    var num = Math.round(Number(price));
    if (isNaN(num)) return escapeHtml(price);
    return '¥' + num.toLocaleString();
  }

  // ── 色語の「形」判定 ──
  // 色辞書には「赤」「黒」「コン」「クロ」のような短い語があり、単純な部分一致だと
  // 「赤ちゃん」→赤、「コンビニ」→紺、「お茶」→茶、「白髪」→白、「クローゼット」→黒 のように、
  // 色を言っていない相談文まで色と誤判定していた。
  // ★ なぜ「誤爆する単語の除外リスト」にしないか:
  //   「赤ちゃん」「コンビニ」…と語を1つずつ除外する方式は、新しい誤爆が見つかるたびに
  //   リストが際限なく増え、色語を辞書に足すたびに除外語も洗い直す必要がある
  //   （この方式は明確に却下済み。今後も足さないこと）。
  //   語を列挙する代わりに、出現箇所の前後の文字を見て「色として使われている形か」で判定する。
  var HIRAGANA_RE = /[ぁ-ゖ]/;
  var KATAKANA_RE = /[ァ-ヺー]/; // 長音「ー」もカタカナ語の一部として扱う（クロ+ーゼット対策）
  var KANJI_RE = /[一-鿿]/;
  var SHORT_KATAKANA_RE = /^[ァ-ヺー]{1,2}$/; // 2文字以下のカタカナ語（クロ・コン・モク・モカ・アカ）
  // 1文字の漢字色の直後にあれば「色の言い方」とみなす文字。
  // 「赤い」「黒の」「紺色」「赤系」「赤っぽい」「真っ赤な」「赤め」「赤み」のような色の語形と、
  // 「緑がいい」「黒ではなく」「赤にします」「赤と青」「赤を探しています」「紺だと嬉しい」の
  // ように色を話題・対象にする助詞・助動詞。
  // ※「を」「だ」も含める（「赤を」「紺だと」は明示的な色指定なので読み落とせない）。
  //   「お茶を」の誤爆は、後続文字を削るのではなく直前の「お」を見るルール側で防ぐ。
  var COLOR_KANJI_NEXT = 'い色系っめみなのがはもでにとかやをだ';

  // 語 w が text の位置 at で「色として」使われている形かどうか。
  // ・1文字の漢字色（赤・黒・白・紺・青・灰・茶・紫・緑・黄）:
  //   直後が COLOR_KANJI_NEXT の文字、または語末・記号・英数なら色とみなす。
  //   直後がそれ以外のかな・漢字なら別の単語の一部（赤ちゃん・白髪・青空・黒板）と判断する。
  //   直前が「お」のときは敬語接頭辞つきの名詞（お茶）とみなす。色に「お」は付かないため、
  //   「お茶を」「お茶が」はこのルールで除外される（COLOR_KANJI_NEXT に「を」を入れても
  //   「お茶を」で誤爆しないのはこのため）。
  //   ※既知の限界: 「茶」は「茶の間」「緑茶が(好き)」のように、直後1文字が色の言い方と
  //   重なる別の単語の一部にもなる。これに対処しようとすると「黒の」「緑が」のような
  //   普通の色の言い方まで読み落とす副作用が出るため、現状は誤読を許容している
  //   （誤って茶色に寄るだけで、提案自体は止まらない）。
  // ・2文字以下のカタカナ語: 前後どちらかがカタカナ・長音なら
  //   別のカタカナ語の一部（クローゼット・コンビニ・モカシン）なので色とみなさない。
  // ・それ以外（3文字以上の語・英字）: 誤爆しにくいので従来どおり常に部分一致扱い。
  function isWordUsageAt(text, w, at) {
    var prev = at > 0 ? text.charAt(at - 1) : '';
    var next = at + w.length < text.length ? text.charAt(at + w.length) : '';

    if (w.length === 1 && KANJI_RE.test(w)) {
      if (prev === 'お') return false;
      if (!next) return true; // 語末（「やっぱり緑」）
      if (COLOR_KANJI_NEXT.indexOf(next) >= 0) return true;
      return !(HIRAGANA_RE.test(next) || KATAKANA_RE.test(next) || KANJI_RE.test(next));
    }

    if (SHORT_KATAKANA_RE.test(w)) {
      if (prev && KATAKANA_RE.test(prev)) return false;
      if (next && KATAKANA_RE.test(next)) return false;
      return true;
    }

    return true;
  }

  // text 内で語 w が「その語として」使われている最後の位置。無ければ -1。
  function lastWordIndexIn(text, w) {
    var found = -1;
    for (var at = text.indexOf(w); at >= 0; at = text.indexOf(w, at + 1)) {
      if (isWordUsageAt(text, w, at)) found = at;
    }
    return found;
  }

  function matchValueToQuery(value, query) {
    if (!query) return false;
    var q = String(query).toLowerCase();
    var v = String(value).toLowerCase();
    for (var i = 0; i < COLOR_GROUPS.length; i++) {
      var g = COLOR_GROUPS[i];
      var inV = g.some(function (w) { return v.indexOf(w.toLowerCase()) >= 0; });
      // 相談文側は「色として使われている形」の出現だけを拾う（「赤ちゃん」「コンビニ」対策）。
      // 商品側の値（バリアント名）は色名そのものなので、従来どおり部分一致でよい。
      var inQ = g.some(function (w) { return lastWordIndexIn(q, w.toLowerCase()) >= 0; });
      if (inV && inQ) return true;
    }
    // 値そのものと相談文の照合も同じ形判定を通す。短いカタカナ値（「コン」等）が
    // 相談文中の別の語（「コンビニ」等）に部分一致で誤爆するのを防ぐため。
    if (v.length >= 2 && lastWordIndexIn(q, v) >= 0) return true;
    var nums = v.match(/\d+/g);
    if (nums) { for (var n = 0; n < nums.length; n++) { if (q.indexOf(nums[n]) >= 0) return true; } }
    return false;
  }

  // 相談文の中で「最後に」言われた色のグループ（COLOR_GROUPS の1要素）。色が無ければ null。
  // ★ 会話が進むと前の色指定も lastUserQuery に残るため（「赤い靴下→やっぱり緑」だと赤も緑も
  //   両方入っている）、matchValueToQuery で商品の色並び順に照合すると、並びの先に来る色
  //   （言い直し前の赤）が選ばれてしまう。各グループの語が文中に出てくる位置を比べ、
  //   いちばん後ろで言われた色を採る。サイズ側（requestedSizesCm）と同じ考え方。
  // ★ 出現位置は「色として使われている形」のものだけを数える
  //   （「赤ちゃん」「コンビニ」等の誤爆対策。詳細は isWordUsageAt のコメント）。
  function lastColorGroupInQuery(query) {
    if (!query) return null;
    var q = String(query).toLowerCase();
    var best = null;
    var bestPos = -1;
    for (var i = 0; i < COLOR_GROUPS.length; i++) {
      var g = COLOR_GROUPS[i];
      for (var j = 0; j < g.length; j++) {
        var pos = lastWordIndexIn(q, g[j].toLowerCase());
        if (pos > bestPos) { bestPos = pos; best = g; }
      }
    }
    return best;
  }

  // ── 会話状態の保存/復元 ──
  // ページを移動しても診断の途中経過が消えないよう、同一タブ内で保持する
  // （sessionStorage＝タブを閉じれば消える。個人情報は保存しない方針に沿う）
  var STORE_KEY = 'tabioAifitState';

  function saveState(data) {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch (e) { /* 保存不可なら諦める */ }
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      // 形式が想定と違うものは捨てる
      if (!d || !Array.isArray(d.messages)) return null;
      return d;
    } catch (e) { return null; }
  }

  function clearState() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
  }

  // ── URL パラメータでの自動オープン ──
  // LINE やメモに貼った URL（例: /?ai-fit=open）から、ワンタップで診断を始められるようにする。
  // フローティング表示のときだけ有効（ページ内設置は元から開いているため何もしない）。
  //
  // ★ 診断結果そのものを共有する機能（?ai-fit=result&p=…）は、ご要望が
  //   「チャットを開いた状態のURLを共有したい」だけと判明したため撤去した（2026-08-25）。
  //   必要になったら feature/share-result の内容を戻すこと。
  var OPEN_PARAM = 'ai-fit';
  var OPEN_PARAM_VALUE = 'open';

  function shouldAutoOpen(isFloating, search) {
    if (!isFloating) return false;
    try {
      var value = new URLSearchParams(search || '').get(OPEN_PARAM);
      return value != null && value.trim().toLowerCase() === OPEN_PARAM_VALUE;
    } catch (e) { return false; }
  }

  // ── セクション1インスタンスの初期化 ──
  function initOne(root) {
    if (!root || root.dataset.aifitReady === '1') return;
    root.dataset.aifitReady = '1';

    var apiBase = (root.dataset.apiBase || '').replace(/\/+$/, '');
    var customerId = root.dataset.customerId || null;
    // ── マスコットの画像（2026-09-08 の支給素材で3コマ化） ──
    // コンシェルジュ … 考えている間は 1→2→3 を繰り返す（目を瞑る動き）
    // リブ           … 選んでいる間は 左→中→右 を繰り返す（首を振る動き）
    // 末尾の end     … 探し終えた合図。結果が出る直前に一瞬だけ出す
    // ★ 左右の移動（コンシェルジュ）は引き続き CSS が担当する。
    // ★ frames が空（JS だけ先に配信され liquid が古いキャッシュ）のときは、
    //   まばたきはせず 1 枚の静止画になる。壊れはしない。
    var conciergeOpen = root.dataset.mascotConciergeOpen || '';
    var conciergeClose = root.dataset.mascotConciergeClose || '';
    var ribUrl = root.dataset.mascotRib || '';
    function frameList(value) {
      return String(value || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    var conciergeFrames = frameList(root.dataset.mascotConciergeFrames);
    var conciergeEnd = root.dataset.mascotConciergeEnd || '';
    // 質問文の下に添える固定画像。先方指定（2026-09-09）で
    // 「ローディング終わり」（c_aifit-concierge-end.png）を使う。
    // 支給素材が無い古いテーマでは従来の open 画像に落ちる。
    var conciergeStatic = conciergeEnd || conciergeFrames[1] || conciergeOpen;
    var ribFrames = frameList(root.dataset.mascotRibFrames);
    var ribEnd = root.dataset.mascotRibEnd || '';
    var thinkingText = root.dataset.thinkingText || 'ご回答を受け取りました。\n次の質問を考えています。';
    var preparingText = root.dataset.preparingText || 'ぴったりの靴下を選んでいます。';

    var isFloating = root.dataset.displayMode === 'floating';
    var launcherBtn = root.querySelector('[data-launcher]');
    var headerEl = root.querySelector('[data-header]');
    var introEl = root.querySelector('[data-intro]');
    var introChipsEl = root.querySelector('[data-intro-chips]');
    var inputAreaEl = root.querySelector('[data-input-area]');
    var messagesEl = root.querySelector('[data-messages]');
    var inputEl = root.querySelector('[data-input]');
    var sendBtn = root.querySelector('[data-send]');
    // 最初から診断をやり直すボタン（相談が始まってから出す）。
    // 古いテーマには要素が無いので、無くても動くようにしてある。
    var restartBtn = root.querySelector('[data-restart]');
    if (!messagesEl || !inputEl || !sendBtn) return;

    // コマ送り用の画像の先読み（切替時のちらつき防止）。
    // ★ 先読みしないと、最初の1周目だけコマが白く抜ける。
    // ★ ページ表示と同時には読まない。マスコットの画像は合計 400KB を超えるため、
    //   フローティング設置では**診断を開かないお客様にも毎ページ落ちてしまう**。
    //   診断を開いた時点（または初回のローディング）でまとめて読む。
    var mascotsPreloaded = false;
    function preloadMascots() {
      if (mascotsPreloaded) return;
      mascotsPreloaded = true;
      // ★ conciergeEnd は質問文の下に置く固定画像として使う（先方指定 2026-09-09）。
      //   ローディングの「探し終えた合図」はリブの担当で、そちらでは使わない。
      [conciergeOpen, conciergeClose, ribUrl, ribEnd, conciergeEnd]
        .concat(conciergeFrames, ribFrames)
        .forEach(function (u) {
          if (u) { var im = new Image(); im.src = u; }
        });
    }

    var state = {
      messages: [],
      sending: false,
      started: false,
      lastUserQuery: '', // 会話全体のユーザー発言を連結して保持する
      initialChips: [],
      stopFrames: null,
      lastResult: null, // 直近の推薦結果（復元時にカードを出し直す）
      // ローディング中のマスコット（コマ送り・探し終えた合図で使う）
      loadingImg: null,
      loadingEndSrc: '',
      endTimer: null,
      // /recommend を遅らせている待ちタイマー。やり直しで取り消せるよう持っておく。
      prepTimer: null,
      /**
       * 会話の世代。やり直し・× で会話を捨てるたびに 1 増える。
       *
       * ★ 通信を始めた時点の値を控えておき、応答が返ったときに今の値と比べる。
       *   違えば「捨てた会話への応答」なので何もしない。
       * ★ state.started（会話中か）では足りない。やり直した直後に新しい相談を
       *   始めると started は再び true になり、古い応答が新しい会話に混ざる。
       *   実際に「捨てたはずの商品カードが新しい会話に現れ、保存までされる」
       *   ことをレビューで再現して確認している（2026-09-08）。
       */
      epoch: 0,
      // 結果カードで選ばれているカラー・サイズ（カードごと・products と同じ並び）。
      // ページを移動して戻ったときに選び直さずに済むよう保存する。
      // 保存するのは「選んだ色名」と「切替先の商品handle」だけ（個人情報は入れない）。
      selections: [],
      // 結果カルーセルの世代。会話を続けて新しい結果が出たあと、
      // 画面に残っている古いカードが新しい選択を上書きしないようにする。
      resultId: 0,
      // いま相談している話題が、messages の何番目から始まったか。
      // /next が「別の商品を新たに探し始めた（isNewTopic）」と判定したら、その発言の位置を入れる。
      // 0 = 会話のはじめから１つの相談が続いている（＝従来どおり会話全体を送る）。
      // ★ これが必要な理由:
      //   話題を切り替えると /next は「どなたがお使いになりますか？」と聞き直すので、
      //   実際に /recommend を呼ぶのは、その答え（例「男性用」）やサイズを答えた
      //   数ターン後になる。そのとき /next が返す isNewTopic は「直前の質問への回答」
      //   なので false。つまりフラグだけを引き継いでも、前の相談（例「女性へのプレゼント」）
      //   が会話履歴に残ったままになり、検索文に女性向けが混ざり続ける。
      //   そこで「どこから今の相談か」を覚えておき、/recommend にはそれ以降だけを送る。
      // ★ 画面のチャット表示（messagesEl）と state.messages は削らない。
      //   送る範囲を絞るだけなので、画面のやりとりや復元は従来どおり全部残る。
      topicStart: 0
    };

    // いまの会話をタブに保存する
    function persist() {
      saveState({
        messages: state.messages,
        lastUserQuery: state.lastUserQuery,
        lastResult: state.lastResult,
        selections: state.selections,
        started: state.started,
        topicStart: state.topicStart
      });
    }

    // API へ送る形に整える。表示用の display はサーバーが見ないので落とす
    // （余計なキーを送らない）。
    function forApi(list) {
      return list.map(function (m) { return { role: m.role, content: m.content }; });
    }

    // いま相談している話題ぶんの会話だけを取り出す（API へ送る範囲）。
    // 範囲がおかしいときは会話全体に倒す（送らないより、従来どおり全部送るほうが安全）。
    function topicMessages() {
      var start = state.topicStart;
      if (typeof start !== 'number' || start < 0 || start >= state.messages.length) return state.messages;
      return start === 0 ? state.messages : state.messages.slice(start);
    }

    // ── 画面遷移 ──
    function enterConversation() {
      if (state.started) return;
      state.started = true;
      if (introEl) introEl.hidden = true;
      if (headerEl) headerEl.hidden = false;
      messagesEl.hidden = false;
      root.classList.remove('is-intro');
      if (inputAreaEl) inputAreaEl.classList.remove('is-intro');
      if (restartBtn) restartBtn.hidden = false;
      inputEl.placeholder = '次の回答を入力ください';
    }

    function resetToIntro() {
      stopLoading();
      // ★ 世代を進める。飛行中の通信は止められないので、返ってきたときに
      //   「捨てた会話への応答」と判断できるようにする。
      state.epoch++;
      state.messages = [];
      state.sending = false;
      state.started = false;
      state.lastUserQuery = '';
      state.lastResult = null;
      state.selections = [];
      state.topicStart = 0; // 会話を捨てるので話題の開始位置も戻す
      state.resultId++; // 消したカードの切替が終わっても選択を書き戻さない
      clearState();
      messagesEl.innerHTML = '';
      messagesEl.hidden = true;
      if (introEl) introEl.hidden = false;
      if (headerEl) headerEl.hidden = true;
      root.classList.add('is-intro');
      if (inputAreaEl) inputAreaEl.classList.add('is-intro');
      if (restartBtn) restartBtn.hidden = true;
      inputEl.value = '';
      inputEl.style.height = 'auto';
      inputEl.placeholder = root.querySelector('[data-input]').getAttribute('aria-label') || '自由にご相談ください';
      renderIntroChips();
      setSendEnabled(false);
    }

    // ── 表示ヘルパ ──
    // ★ 実際にスクロールする器を返す。
    //   以前は会話エリア（messagesEl）自身がスクロールしていたが、
    //   「スクロールする場所が全体じゃないのが変」との指摘（2026-08-12）で
    //   パネル全体スクロールに変更したため、器は
    //     フローティング … パネル（overflow-y:auto）
    //     ページ内設置   … ページ
    //   になる。CSS を変えても JS が追随するよう、毎回たどって決める。
    function scroller() {
      var el = messagesEl;
      while (el && el !== document.body) {
        var oy = window.getComputedStyle(el).overflowY;
        if (oy === 'auto' || oy === 'scroll') return el;
        el = el.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    }

    // 器の中での要素の位置（器の上端を 0 とした座標）
    function offsetInScroller(el, s) {
      var base = (s === document.scrollingElement || s === document.documentElement)
        ? 0 : s.getBoundingClientRect().top;
      return s.scrollTop + el.getBoundingClientRect().top - base;
    }

    function scrollToBottom() {
      setTimeout(function () { var s = scroller(); s.scrollTop = s.scrollHeight; }, 30);
    }

    // やり直しで TOP に戻したとき、画面が下の方を向いたままにならないようにする
    function scrollToTop() {
      setTimeout(function () { var s = scroller(); s.scrollTop = 0; }, 30);
    }

    function addUserMessage(text) {
      var div = document.createElement('div');
      div.className = 'tabio-aifit__msg tabio-aifit__msg--user';
      div.innerHTML = '<div class="tabio-aifit__bubble">' + escapeHtml(text) + '</div>';
      messagesEl.appendChild(div);
      scrollToBottom();
    }

    // AI 発話は吹き出しなしの素テキスト（Figma 準拠）
    //
    // withMascot: 発話の下にコンシェルジュ（雲）の固定画像を添えるか。
    // ★ 支給デザイン（AIfit画像_使用シーンイメージ.pdf の2枚目）では、AI の
    //   質問文の下に【コンシェルジュ固定画像】を置く指定になっている。
    //   エラー文や結果の締めの一言には添えない（質問のときだけ）。
    function addBotMessage(text, extraClass, withMascot) {
      var div = document.createElement('div');
      div.className = 'tabio-aifit__msg tabio-aifit__msg--bot';
      div.innerHTML = '<div class="tabio-aifit__bot-text ' + (extraClass || '') + '">' +
        escapeHtml(text).replace(/\n/g, '<br>') + '</div>';
      if (withMascot && conciergeStatic) {
        var m = document.createElement('img');
        m.className = 'tabio-aifit__mascot tabio-aifit__mascot--concierge-static';
        m.src = conciergeStatic;
        m.alt = '';
        m.setAttribute('aria-hidden', 'true');
        div.appendChild(m);
      }
      messagesEl.appendChild(div);
      scrollToBottom();
      return div;
    }

    function addError(text) {
      addBotMessage(text, 'tabio-aifit__error');
    }

    // 「よくあるご質問」へのリンクを AI 発話の下に添える。
    // ★ 発話テキストは escapeHtml で無害化しているため、文中に <a> は書けない
    //   （セキュリティレビュー指摘6）。リンクは要素として組み立てて足す。
    //   URL・文言はテーマ設定（セクション）から取るので、ストアごとに変えられる。
    function addFaqLink() {
      var url = (root.dataset.faqUrl || '').trim();
      if (!url) return; // 未設定なら何も出さない（リンク切れを作らない）
      var label = (root.dataset.faqLabel || '').trim() || 'よくあるご質問を見る';
      var div = document.createElement('div');
      div.className = 'tabio-aifit__msg tabio-aifit__msg--bot';
      var a = document.createElement('a');
      a.className = 'tabio-aifit__faq-link';
      a.href = url;                 // 属性経由なので文字列連結による HTML 混入は起きない
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = label;
      div.appendChild(a);
      messagesEl.appendChild(div);
      scrollToBottom();
    }

    // ── ローディング（マスコットのコマ送り） ──
    // kind='think' → コンシェルジュ3コマ / kind='prep' → リブ3コマ（どちらも JS で差し替え）
    function startLoading(kind) {
      preloadMascots(); // ページ内設置や復元の経路でもここで確実に読む
      stopLoading();
      var isThink = kind !== 'prep';
      var text = isThink ? thinkingText : preparingText;

      var wrap = document.createElement('div');
      wrap.className = 'tabio-aifit__msg tabio-aifit__msg--bot';
      wrap.setAttribute('data-loading', '');

      var box = document.createElement('div');
      box.className = 'tabio-aifit__loading';
      box.innerHTML =
        '<div class="tabio-aifit__loading-text">' + escapeHtml(text).replace(/\n/g, '<br>') + '</div>';

      var img = document.createElement('img');
      img.className = 'tabio-aifit__mascot tabio-aifit__mascot--' + (isThink ? 'concierge' : 'rib');
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');

      // コマ送りを回す。frames が空なら 1 枚だけ置いて動かさない。
      //
      // ★ 往復で回す（1→2→3→2→1→2→…）。片道（1→2→3→1）だと、リブは
      //   1コマ目=右／3コマ目=左に首を振っているため、左端から右端へ瞬間移動して
      //   見える。置き換え前の CSS（taf-sway）も -5° ⇄ +5° の往復だった。
      // ★ 止まっているときに出す絵は中央（2コマ目）にする。首を振り切ったコマで
      //   止まると不自然なため。
      function playFrames(frames, fallbackSrc) {
        // ★ URL が無いときは img を置かない。空の src を入れると、ブラウザに
        //   よってはページ自体を取りに行き、幅だけある空箱が残る。
        if (!frames.length) {
          if (fallbackSrc) img.src = fallbackSrc;
          return;
        }
        // 止まっているときに出すのは真ん中のコマ（首を振り切った端では不自然）。
        // ★ コマ数が偶数だと floor では中央にならないので、往復の順路の
        //   ちょうど半分の位置を使う。
        var mid = Math.floor((frames.length - 1) / 2);
        if (reduceMotion || frames.length < 2) { img.src = frames[mid]; return; }

        // 往復の順路をあらかじめ作る（3コマなら 0,1,2,1）
        var order = frames.map(function (_, i) { return i; });
        for (var k = frames.length - 2; k > 0; k--) order.push(k);

        var i = 0;
        img.src = frames[order[0]];
        var timer = setInterval(function () {
          i = (i + 1) % order.length;
          img.src = frames[order[i]];
        }, FRAME_INTERVAL_MS);
        state.stopFrames = function () { clearInterval(timer); };
      }

      if (isThink) {
        // 左右の移動は CSS アニメーション。JS はコマの差し替えだけ担当する
        var track = document.createElement('div');
        track.className = 'tabio-aifit__mascot-track';
        track.appendChild(img);
        box.appendChild(track);
        playFrames(conciergeFrames, conciergeOpen);
      } else {
        box.appendChild(img);
        playFrames(ribFrames, ribUrl);
      }
      // 探し終えた合図の絵を出すために、いま何を表示しているかを覚えておく
      state.loadingImg = img;
      state.loadingEndSrc = isThink ? conciergeEnd : ribEnd;
      wrap.appendChild(box);
      messagesEl.appendChild(wrap);
      scrollToBottom();
    }

    function stopLoading() {
      if (state.stopFrames) { state.stopFrames(); state.stopFrames = null; }
      if (state.endTimer) {
        clearTimeout(state.endTimer);
        state.endTimer = null;
        // ★ 待ちを取り消したら、待っている Promise も必ず終わらせる。
        //   放置すると解決も拒否もされないまま残る。
        if (state.endResolve) { state.endResolve(); state.endResolve = null; }
      }
      // ★ /recommend を遅らせている待ちも取り消す。残すと、やり直したあとに
      //   捨てた会話の商品検索が発射される（AI の呼び出し＝費用も無駄になる）。
      if (state.prepTimer) { clearTimeout(state.prepTimer); state.prepTimer = null; }
      state.loadingImg = null;
      state.loadingEndSrc = '';
      var el = messagesEl.querySelector('[data-loading]');
      if (el) el.remove();
    }

    /**
     * 「探し終えた合図」の絵を一瞬だけ見せてから done() を呼ぶ。
     * コマ送りを止めて end の絵に差し替え、少し待ってからローディングを消す。
     *
     * ★ 合図の絵が無い／動きを減らす設定のときは、待たずにすぐ done() を呼ぶ。
     *   待たせるのは演出であって、待たせること自体に意味は無いため。
     */
    function finishLoading(done) {
      var img = state.loadingImg;
      var endSrc = state.loadingEndSrc;
      if (!img || !endSrc || reduceMotion) {
        stopLoading();
        return Promise.resolve().then(done);
      }
      if (state.stopFrames) { state.stopFrames(); state.stopFrames = null; }
      img.src = endSrc;
      // ★ Promise を返すこと。setTimeout の中で done() を呼びっぱなしにすると、
      //   done() が投げた例外が呼び出し元の .catch に届かず、送信中の状態
      //   （state.sending）が true のまま固まって入力できなくなる。
      return new Promise(function (resolve) {
        // 途中で stopLoading された場合にも終わらせられるよう控えておく
        state.endResolve = resolve;
        state.endTimer = setTimeout(function () {
          state.endTimer = null;
          state.endResolve = null;
          stopLoading();
          resolve();
        }, LOADING_END_HOLD_MS);
      }).then(done);
    }

    function disableAllChips() {
      var chips = root.querySelectorAll('.tabio-aifit__chip');
      for (var i = 0; i < chips.length; i++) chips[i].disabled = true;
    }

    // ── 選択肢チップ ──
    function buildChip(v, onPick) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tabio-aifit__chip';
      btn.textContent = v.label;
      btn.addEventListener('click', function () {
        if (state.sending || btn.disabled) return;
        btn.classList.add('is-selected');
        onPick(v);
      });
      return btn;
    }

    function normalizeChoices(choices) {
      var valid = [];
      for (var i = 0; i < (choices || []).length; i++) {
        var c = choices[i];
        var label = (c && c.label != null) ? String(c.label).trim() : '';
        if (!label) continue;
        var kw = (c && c.keyword != null) ? String(c.keyword).trim() : '';
        valid.push({ label: label, keyword: kw || label });
      }
      return valid;
    }

    // TOP の初期チップ（入力欄の下・横スクロール）
    function renderIntroChips() {
      if (!introChipsEl) return;
      introChipsEl.innerHTML = '';
      state.initialChips.forEach(function (v) {
        introChipsEl.appendChild(buildChip(v, function (picked) {
          handleUserTurn(picked.label, picked.keyword);
        }));
      });
    }

    // ── 通信 ──
    function postJson(path, body) {
      var payload = {};
      for (var k in body) { if (Object.prototype.hasOwnProperty.call(body, k)) payload[k] = body[k]; }
      if (customerId) payload.customerId = customerId;
      return fetch(apiBase + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        return r.json().then(function (d) {
          if (!r.ok) throw new Error((d && d.error) || '通信エラーが発生しました。');
          return d;
        }, function () {
          throw new Error('通信エラーが発生しました。');
        });
      });
    }

    // ── フロー ──
    function loadConfig() {
      if (!apiBase) return;
      fetch(apiBase + '/api/diagnosis/config', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cfg) {
          if (cfg && Array.isArray(cfg.initialChips)) {
            state.initialChips = normalizeChoices(cfg.initialChips);
            renderIntroChips();
          }
        })
        .catch(function () { /* チップ取得失敗でも自由入力は可能 */ });
    }

    function handleUserTurn(displayText, contentText) {
      if (state.sending) return;
      var content = String(contentText || displayText || '').trim().slice(0, MAX_CONTENT);
      if (!content) return;

      enterConversation();
      disableAllChips();
      // 直前の発言だけを保持していたため、「黄色い靴下」→「カジュアル」と
      // 会話が進むと色の指定が消え、カードの色が初期選択されなかった。
      // 会話全体のユーザー発言を連結して保持する。
      state.lastUserQuery = (state.lastUserQuery + ' ' + String(displayText || content)).trim();
      addUserMessage(displayText || content);
      // ★ 表示用の文言（チップのラベル）も持たせる。
      //   content は API に送る内部値＝チップの keyword（例 male / business）で、
      //   画面に出す文言とは別物。これを保存していなかったため、
      //   更新（リロード）で復元したときに吹き出しが英語のキーワードになっていた。
      var shown = String(displayText || content).slice(0, MAX_CONTENT);
      state.messages.push(
        shown === content
          ? { role: 'user', content: content }
          : { role: 'user', content: content, display: shown }
      );
      persist();

      runNext();
    }

    function afterTurnReady() {
      state.sending = false;
      setSendEnabled(inputEl.value.trim().length > 0);
    }

    // 【防御】messages が空のまま API を呼ばない。
    // バックエンドは messages 空配列を受け取ると「メッセージを入力してください。」を
    // 返す（src/lib/apiGuards.ts）。それが会話の先頭に赤いエラーとして出てしまう事象の報告があった。
    // handleUserTurn は必ず1件 push してから runNext() を呼ぶので通常は起こらないが、
    // 古いJSがブラウザキャッシュに残っている等の状況では起こり得る。
    // ★ お客様から見れば何も起きていないので、エラーは出さない（赤い表示のほうが不自然）。
    //   代わりに入力可能な状態へ戻し、行き止まりにしない。
    function hasMessages() {
      return !!(state.messages && state.messages.length);
    }

    function runNext() {
      if (!hasMessages()) {
        stopLoading();
        afterTurnReady();
        return;
      }
      state.sending = true;
      setSendEnabled(false);
      startLoading('think');

      // ★ 通信を始めた時点の世代。応答が返ったとき、やり直し等で会話が
      //   捨てられていれば違う値になっている。捨てた会話の結果を新しい会話へ
      //   混ぜないための照合に使う。
      var myEpoch = state.epoch;

      postJson('/api/diagnosis/next', { messages: forApi(state.messages) })
        .then(function (data) {
          // 捨てた会話への応答なら何もしない（画面にも保存にも触らない）
          if (myEpoch !== state.epoch) return;

          // 話題が切り替わったかの判定は /next だけが行う。
          // ここは受け取った結論を覚えて /recommend に渡すだけで、判定はしない。
          // 返ってこない（＝判定していない回、または古いサーバー）ときは null にして、
          // /recommend には何も渡さない。その場合 /recommend は従来どおり自分で判定する。
          var judged = (data && typeof data.isNewTopic === 'boolean') ? data.isNewTopic : null;
          if (judged === true) {
            // この時点の messages の末尾は、いま送ったお客様の発言そのもの
            // （返信のバブルはこのあとで push される）。そこが新しい相談の開始位置。
            state.topicStart = Math.max(0, state.messages.length - 1);
          }

          if (data && data.done) {
            runRecommend(judged);
            return;
          }
          stopLoading();
          var q = (data && data.question ? String(data.question) : '').trim();
          if (q) {
            // 支給デザインどおり、質問文の下にコンシェルジュの固定画像を添える
            addBotMessage(q, null, true);
            state.messages.push({ role: 'assistant', content: q.slice(0, MAX_CONTENT) });
          }
          // 配送・返品など診断では答えられないご質問のときだけ、FAQ のリンクを添える。
          if (data && data.faqGuide) addFaqLink();
          // ★ 聞き返しの選択肢（data.choices）は描かない。追加質問にはすべて
          //   自由入力で答えてもらう（先方依頼 2026-10-16「すべて会話ベースに」）。
          persist();
          afterTurnReady();
        })
        .catch(function (err) {
          if (myEpoch !== state.epoch) return;
          stopLoading();
          addError(err.message || 'エラーが発生しました。');
          afterTurnReady();
        });
    }

    // newTopic: 直前の /next が返した「新しい話題か」の判定（判定が無ければ null）。
    // 引き継ぐのは true のときだけ。false と null はフィールド自体を送らず、
    // /recommend は従来どおり自分で判定する（理由は下のボディ組み立てのコメント）。
    function runRecommend(newTopic) {
      // runNext() と同じ防御（理由は hasMessages のコメント参照）。
      if (!hasMessages()) {
        stopLoading();
        afterTurnReady();
        return;
      }
      // 送るのは「いま相談している話題ぶん」だけ（topicMessages のコメント参照）。
      var body = { messages: forApi(topicMessages()) };
      // ★ 引き継ぐのは true のときだけ。false は送らない。
      //   話題の切り替え判定は /next と /recommend の2か所にあり、実データで的中率を
      //   測ると /next 83% / /recommend 89% で /recommend の方が当たる。とくに
      //   「やっぱり自分用にスポーツ用の靴下がほしい」のような、ほのめかす切り替えは
      //   /next が3回中0回しか当てられず、/recommend は3回中3回当てた。
      //   そのため /next の false を引き継ぐと、当たるほうの判定を潰してしまう
      //   （/recommend 側は「判定済み」として自分では判定しなくなるため）。
      //   true だけ引き継げば、/next が切り替えを検知できた回は確実にリセットでき
      //   （会話の切り出し＝topicStart も効く）、検知できなかった回は /recommend の
      //   判定に任せられる。
      if (newTopic === true) body.newTopic = true;
      // 「質問を考えています」→「商品を選んでいます」でマスコットが切り替わる
      startLoading('prep');
      // ★ 通信を始めた時点の世代（理由は runNext のコメント参照）。
      var myEpoch = state.epoch;
      // ★ タイマーを控えること。控えないと、やり直しを 700ms 以内に押したときに
      //   捨てた会話の商品検索が発射される（AI の呼び出し＝費用も無駄になる）。
      state.prepTimer = setTimeout(function () {
        state.prepTimer = null;
        if (myEpoch !== state.epoch) return;
        postJson('/api/diagnosis/recommend', body)
          .then(function (data) {
            if (myEpoch !== state.epoch) return;
            root.dispatchEvent(new CustomEvent('tabio:aifit:recommended', { bubbles: true }));
            var msg = (data && data.message ? String(data.message) : '').trim();
            var products = (data && Array.isArray(data.products)) ? data.products : [];
            // 推薦に使ったお客様のサイズ（cm）。会話で言われたもの・過去の診断記録で補ったもの。
            // 古い API（項目が無い）・分からない回は null＝従来どおり会話から決める。
            var customerSizeCm = customerSizeCmOf(data && data.customerSizeCm);
            if (products.length) {
              // 先にストアから色・サイズを取得してから描画する。
              // 後から取得すると、初期選択に合わせて画像が入れ替わるのが見えてしまう。
              // 待っている間はローディング表示のままなので体感の遅れは小さい。
              return prefetchVariants(products).then(function (variantMap) {
                // ★ 商品が見つかったときだけ「探し終えた合図」の絵を一瞬見せてから出す
                //   （2026-09-08 の支給素材。見つからなかったときは出さない）。
                // ★ return すること。返さないと、この中で起きた例外が下の .catch に
                //   届かず、送信中の状態が固まって入力できなくなる。
                return finishLoading(function () {
                  // ★ 合図の絵を見せている 700ms のあいだにも、やり直される
                  //   ことがある。ここでもう一度だけ世代を確かめる。
                  if (myEpoch !== state.epoch) return;

                  if (msg) addBotMessage(msg);
                  // 新しい結果カードなので選択は作り直す。世代を進めて、
                  // 画面に残っている前回のカードと切り離す。
                  // ★ at＝この結果が会話の何番目に入るか。
                  //   カード一式（導入文・カード・締めの一言）は state.messages に積んでいないため、
                  //   位置を覚えておかないと復元時に「会話の最後」へ足すしかなくなる。
                  //   結果が出たあとに会話を続けると、更新でカードが最新の質問より下に飛んでいた。
                  //   この時点の messages の末尾は、いま結果を出すきっかけになったお客様の発言。
                  state.lastResult = {
                    products: products,
                    message: msg,
                    at: state.messages.length,
                    customerSizeCm: customerSizeCm
                  };
                  state.selections = [];
                  state.resultId++;
                  renderResult(products, variantMap, null, customerSizeCm);
                  addBotMessage('他になにかお手伝いできることはありますか？');
                  persist();
                  afterTurnReady();
                });
              });
            }
            stopLoading();
            if (msg) addBotMessage(msg);
            addBotMessage('ご希望に合う商品が見つかりませんでした。条件を変えてもう一度お試しください。');
            addBotMessage('他になにかお手伝いできることはありますか？');
            afterTurnReady();
          })
          .catch(function (err) {
            if (myEpoch !== state.epoch) return;
            stopLoading();
            addError(err.message || '推薦処理でエラーが発生しました。');
            afterTurnReady();
          });
      }, 700);
    }

    // ── 結果（リプ＋商品カルーセル） ──
    // savedSelections: 保存されていたカードごとの選択（復元時のみ。通常は null）
    // customerSizeCm: 推薦に使ったお客様のサイズ（cm。/recommend の customerSizeCm。無ければ null）
    function renderResult(products, variantMap, savedSelections, customerSizeCm) {
      // このカルーセルの世代。あとから新しい結果が出たら、こちらの記録は止める。
      var myResultId = state.resultId;

      // カードで選ばれた色・サイズを控える（カードの並び＝products の並び）
      function recordSelection(index, value) {
        if (myResultId !== state.resultId) return; // 古いカルーセルは書き込まない
        state.selections[index] = value;
        persist();
      }

      var wrap = document.createElement('div');
      wrap.className = 'tabio-aifit__msg tabio-aifit__msg--bot';

      var result = document.createElement('div');
      result.className = 'tabio-aifit__result';

      if (ribUrl) {
        var rib = document.createElement('img');
        rib.className = 'tabio-aifit__mascot tabio-aifit__mascot--rib-static';
        rib.src = ribUrl;
        rib.alt = '';
        rib.setAttribute('aria-hidden', 'true');
        result.appendChild(rib);
      }

      var list = document.createElement('div');
      // ★ 2 件以下は Figma の 2 カラム（6091:156）で並べる。3 件以上は横スクロール
      //   （6091:71）。どちらも SP 375px 幅の設計で、幅ではなく件数で切り替わる。
      //   PC は 3 件でも 770px に収まるので、この印だけ付けて見た目は変えない。
      list.className = 'tabio-aifit__products' +
        (products.length <= 2 ? ' tabio-aifit__products--grid' : '');

      var html = '';
      for (var i = 0; i < products.length; i++) {
        var p = products[i] || {};
        var url = p.productUrl || (p.handle ? '/products/' + p.handle : '#');
        html += '<div class="tabio-aifit__card" data-handle="' + escapeHtml(p.handle || '') + '">';
        html += '<a class="tabio-aifit__card-imglink" href="' + escapeHtml(url) + '" target="_blank" rel="noopener">';
        if (p.imageUrl) {
          html += '<img class="tabio-aifit__card-img" src="' + escapeHtml(p.imageUrl) +
            '" alt="' + escapeHtml(p.title || '') + '" loading="lazy">';
        } else {
          html += '<div class="tabio-aifit__card-noimg">No Image</div>';
        }
        html += '</a>';
        html += '<div class="tabio-aifit__card-info">';
        html += '<div class="tabio-aifit__card-title">' + escapeHtml(p.title || '') + '</div>';
        html += '<div class="tabio-aifit__card-price">' + formatPrice(p.price) + '</div>';
        // p.recommendation は Figma・要件どちらにも無いためカードには表示しない。おすすめ文はカルーセル上部の導入文（message）が担う
        html += '</div>';
        html += '<div class="tabio-aifit__variant" hidden></div>';
        html += '</div>';
      }
      list.innerHTML = html;

      // 4件目以降が画面外にあることが分かるよう、右送りボタンを重ねる
      var carousel = document.createElement('div');
      carousel.className = 'tabio-aifit__carousel';
      carousel.appendChild(list);

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'tabio-aifit__carousel-next';
      next.setAttribute('aria-label', '次の商品を見る');
      next.innerHTML = NEXT_SVG;
      next.addEventListener('click', function () {
        // カード1枚分＋隙間をスクロール
        var card = list.querySelector('.tabio-aifit__card');
        var step = card ? card.getBoundingClientRect().width + 15 : list.clientWidth * 0.8;
        list.scrollBy({ left: step, behavior: 'smooth' });
      });
      carousel.appendChild(next);

      function syncNext() {
        // 右端まで送ったら隠す（1px の誤差を許容）
        var atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
        next.hidden = atEnd || list.scrollWidth <= list.clientWidth + 1;
      }
      list.addEventListener('scroll', syncNext);
      window.addEventListener('resize', syncNext);
      setTimeout(syncNext, 60);

      result.appendChild(carousel);

      wrap.appendChild(result);
      messagesEl.appendChild(wrap);
      enhanceCards(list, products, variantMap, savedSelections, recordSelection, customerSizeCm);

      // 結果は「リプ＋カルーセル」の上端が見えるようにスクロールする。
      // 末尾までスクロールするとマスコットが画面外へ流れてしまうため。
      setTimeout(function () {
        var s = scroller();
        s.scrollTop = Math.max(0, offsetInScroller(wrap, s) - 8);
      }, 40);
    }

    // 商品データ（/products/{handle}.js）のキャッシュ。同じ商品を何度も取りに行かない。
    // サイズ切替時に切替先の商品を取るのにも使う（取得の仕組みは先読みと共通）。
    var productCache = {};

    function fetchProduct(handle) {
      if (!handle) return Promise.resolve(null);
      if (productCache[handle]) return Promise.resolve(productCache[handle]);
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p) productCache[handle] = p; return p; })
        .catch(function () { return null; }); // 他店の商品などは無視（カードは出す）
    }

    // 商品の色/サイズをまとめて先読みする（描画前に揃える）
    function prefetchVariants(products) {
      return Promise.all(products.map(function (p) {
        return fetchProduct(p.handle || '');
      })).then(function (list) {
        var map = {};
        products.forEach(function (p, i) { if (list[i]) map[p.handle] = list[i]; });
        return map;
      });
    }

    // 保存された選択のうち、このカード（＝この商品）のものだけを取り出す。
    // 並びや商品が変わっていたら使わない（別の商品の色を当ててしまわないように）。
    function savedSelectionFor(list, index, cardHandle) {
      var s = list && list[index];
      if (!s || typeof s !== 'object') return null;
      if (s.card && cardHandle && s.card !== cardHandle) return null;
      return s;
    }

    // 各カードに /products/{handle}.js でバリアント（色/サイズ）を読み込む（同一オリジン）
    function enhanceCards(container, products, variantMap, savedSelections, onSelect, customerSizeCm) {
      var cards = container.querySelectorAll('.tabio-aifit__card');
      Array.prototype.forEach.call(cards, function (card, i) {
        var handle = card.getAttribute('data-handle');
        var box = card.querySelector('.tabio-aifit__variant');
        if (!handle || !box) return;
        var prod = variantMap && variantMap[handle];
        // sizeOptions は API が返すサイズ違いの一覧（fitDiagnosis の FitProduct）。
        // カードは products の順に作っているので、同じ添字で対応が取れる。
        if (prod) {
          renderVariantUI(card, box, prod, (products[i] || {}).sizeOptions,
            savedSelectionFor(savedSelections, i, handle),
            function (value) { onSelect(i, value); },
            customerSizeCm);
        }
      });
    }

    // ── サイズ選択（サイズ違いは別商品） ──
    // タビオ様はサイズ違いを別商品として登録しているため、「サイズを選ぶ＝商品を切り替える」。
    // 切替先のバリアント（カート追加に使う ID）は既存の /products/{handle}.js から取る。

    function normalizeSizeOptions(list) {
      var out = [];
      (list || []).forEach(function (o) {
        if (!o || !o.handle) return;
        out.push({ size: o.size == null ? '' : String(o.size), handle: String(o.handle) });
      });
      return out;
    }

    // 相談文に出てきたサイズ（cm）。「M（22〜24.5cm）」のような選択肢の文言も読める。
    // ★ 会話が進むと前のサイズ指定も lastUserQuery に残るため、最後に出てきたものを採る。
    var QUERY_SIZE_RE = /(\d{1,3}(?:\.\d)?)\s*(?:[〜～~\-‐–—―ー−]\s*(\d{1,3}(?:\.\d)?)\s*)?(?:cm|センチ|㎝)/g;

    function requestedSizesCm(query) {
      var text = String(query == null ? '' : query);
      var found = [];
      var m;
      QUERY_SIZE_RE.lastIndex = 0;
      while ((m = QUERY_SIZE_RE.exec(text)) !== null) {
        found = [Number(m[1])];
        if (m[2] != null) found.push(Number(m[2]));
      }
      return found;
    }

    // サイズ表記の範囲（「22.5〜24.5cm」→[22.5,24.5] /「26cm」→[26,26]）。読めなければ null。
    function sizeRangeOf(size) {
      var nums = String(size == null ? '' : size).match(/\d{1,3}(?:\.\d)?/g);
      if (!nums || !nums.length) return null;
      if (nums.length >= 2) return [Number(nums[0]), Number(nums[1])];
      return [Number(nums[0]), Number(nums[0])];
    }

    // 相談文のサイズに合う選択肢（無ければ null＝推薦された商品のまま）
    function pickSizeChoice(choices, query) {
      var cms = requestedSizesCm(query);
      if (!cms.length) return null;
      return pickSizeChoiceByCms(choices, cms);
    }

    // API の customerSizeCm（推薦に使ったお客様のサイズ）を数値にそろえる。使えない値は null。
    function customerSizeCmOf(value) {
      return (typeof value === 'number' && isFinite(value) && value > 0) ? value : null;
    }

    // サイズ（cm）の並びのどれかが範囲に入る選択肢（無ければ null）
    function pickSizeChoiceByCms(choices, cms) {
      for (var i = 0; i < choices.length; i++) {
        var r = sizeRangeOf(choices[i].size);
        if (!r) continue;
        for (var j = 0; j < cms.length; j++) {
          if (cms[j] >= r[0] && cms[j] <= r[1]) return choices[i];
        }
      }
      return null;
    }

    // savedSel: 保存されていたこのカードの選択（{card,handle,options}／無ければ null）
    // onSelect: 選択が決まるたびに呼ぶ（保存用）
    // customerSizeCm: 推薦に使ったお客様のサイズ（cm。無ければ null）
    function renderVariantUI(card, box, prod, sizeOptions, savedSel, onSelect, customerSizeCm) {
      if (!prod || !(prod.variants || []).filter(Boolean).length) return;

      box.hidden = false;

      // いまカート追加・商品リンクの対象になっている商品の情報。サイズを変えると差し替わる。
      var variants = [];     // バリアント
      var optNames = [];     // オプション名
      var optValues = [];    // オプション値
      var sel = [];          // 選択値（オプション位置ごと）
      var sizeIdx = -1;      // サイズのオプション位置（無ければ -1）
      var switching = false; // 切替先の商品を取得中

      // サイズの選択肢は API の sizeOptions で作る。
      // 空（旧APIなど）なら従来どおり商品自身のサイズ値で作る。
      var sizeChoices = normalizeSizeOptions(sizeOptions);
      var selectedHandle = prod.handle || card.getAttribute('data-handle') || '';
      var cardHandle = card.getAttribute('data-handle') || ''; // 保存の照合キー（元の商品）

      // 保存の見出しにオプション名を使う（位置だけだと商品側の並びが変わると崩れるため）
      function optKey(oi) {
        var n = optNames[oi];
        return (n == null || n === '') ? ('#' + oi) : String(n);
      }

      // いまの選択を保存用の形にする。
      // サイズは選択中の handle が表すので options には入れない（＝カラー等だけ）。
      function currentSelection() {
        var opts = {};
        optValues.forEach(function (vals, oi) {
          if (oi === sizeIdx) return;
          if (sel[oi] != null) opts[optKey(oi)] = sel[oi];
        });
        return { card: cardHandle, handle: selectedHandle, options: opts };
      }

      function recordSelection() {
        if (typeof onSelect === 'function') onSelect(currentSelection());
      }

      // 保存された handle が、いまも選べるサイズかどうか
      function isKnownSize(handle) {
        for (var i = 0; i < sizeChoices.length; i++) {
          if (sizeChoices[i].handle === handle) return true;
        }
        return false;
      }

      var imgEl = card.querySelector('.tabio-aifit__card-img');
      var priceEl = card.querySelector('.tabio-aifit__card-price');
      var linkEl = card.querySelector('.tabio-aifit__card-imglink');
      var linkHref = (linkEl && linkEl.getAttribute('href')) || '';

      // 商品データからオプション名・値・選択値を読み込む。
      // prevSel があるとき（サイズ切替時）はカラー等の選択を引き継ぐ。
      function readProduct(p, prevSel) {
        var vs = (p && p.variants ? p.variants : []).filter(Boolean);
        if (!vs.length) return false;

        var rawOpts = p.options || [];
        var names = rawOpts.map(function (o) { return (typeof o === 'string') ? o : o.name; });
        var count = names.length || (vs[0].option3 ? 3 : (vs[0].option2 ? 2 : 1));

        var values = [];
        for (var pos = 1; pos <= count; pos++) {
          var seen = {}, vals = [];
          vs.forEach(function (v) {
            var val = v['option' + pos];
            if (val != null && !seen[val]) { seen[val] = 1; vals.push(val); }
          });
          values.push(vals);
        }

        variants = vs;
        optNames = names;
        optValues = values;
        sizeIdx = sizeOptionIndex(names, values);
        // 初回の事前選択で使う「最後に言われた色」（言い直し対応）。詳細は lastColorGroupInQuery のコメント。
        var lastColorGroup = lastColorGroupInQuery(state.lastUserQuery);
        sel = values.map(function (vals, oi) {
          // 保存された選択が最優先。お客様が自分で選んだものなので、
          // 診断の事前選択より優先して戻す（サイズは handle が表すので対象外）。
          if (savedSel && oi !== sizeIdx && savedSel.options) {
            var saved = savedSel.options[optKey(oi)];
            if (saved != null && vals.indexOf(saved) >= 0) return saved;
          }
          if (prevSel) {
            // 切替先でも同じカラーがあれば引き継ぐ（無ければ先頭）。
            // サイズは切替先の商品自身の値にする（商品ごとに1つ）。
            var keep = prevSel[oi];
            if (oi !== sizeIdx && keep != null && vals.indexOf(keep) >= 0) return keep;
            return vals[0];
          }
          // 初回は相談文に色/サイズ指定があれば事前選択（無ければ先頭）
          // ★ 会話が進むと前の色指定も lastUserQuery に残るため、まず「最後に言われた色」の
          //   グループに属する値を探す（言い直しを反映する。詳細は lastColorGroupInQuery のコメント）。
          //   色が言われていない・この商品にその色が無いときは、従来どおり下の照合に任せる
          //   （サイズ等の他オプションの事前選択もこれまでどおり下の照合で行われる）。
          if (lastColorGroup) {
            for (var c = 0; c < vals.length; c++) {
              var lv = String(vals[c]).toLowerCase();
              for (var w = 0; w < lastColorGroup.length; w++) {
                if (lv.indexOf(lastColorGroup[w].toLowerCase()) >= 0) return vals[c];
              }
            }
          }
          for (var k = 0; k < vals.length; k++) {
            if (matchValueToQuery(vals[k], state.lastUserQuery)) return vals[k];
          }
          return vals[0];
        });
        return true;
      }

      // どのオプションが「サイズ」か。名前で見分け、名前で分からなければ
      // API が返した自分のサイズ表記と同じ値を持つ位置を探す。
      function sizeOptionIndex(names, values) {
        for (var i = 0; i < names.length; i++) {
          var n = String(names[i] == null ? '' : names[i]).toLowerCase();
          if (n.indexOf('サイズ') >= 0 || n.indexOf('size') >= 0) return i;
        }
        var mine = '';
        for (var k = 0; k < sizeChoices.length; k++) {
          if (sizeChoices[k].handle === selectedHandle) mine = sizeChoices[k].size;
        }
        if (mine) {
          for (var j = 0; j < values.length; j++) if (values[j].indexOf(mine) >= 0) return j;
        }
        return -1;
      }

      function matched() {
        return variants.filter(function (v) {
          for (var pos = 1; pos <= optValues.length; pos++) {
            if (v['option' + pos] !== sel[pos - 1]) return false;
          }
          return true;
        })[0] || null;
      }

      function render() {
        var html = '';
        optValues.forEach(function (vals, oi) {
          var label = optNames[oi] || ('オプション' + (oi + 1));
          // サイズはサイズ違いの商品一覧から作る（選択肢が1つでもドロップダウンのまま）
          var useSizes = (oi === sizeIdx && sizeChoices.length > 0);
          var count = useSizes ? sizeChoices.length : vals.length;
          html += '<div class="tabio-aifit__select-wrap">';
          html += '<select class="tabio-aifit__select" ' +
            (useSizes ? 'data-size-select' : 'data-oi="' + oi + '"') +
            ' aria-label="' + escapeHtml(label) + '"' + (count <= 1 ? ' disabled' : '') + '>';
          if (useSizes) {
            sizeChoices.forEach(function (o) {
              html += '<option value="' + escapeHtml(o.handle) + '"' +
                (o.handle === selectedHandle ? ' selected' : '') + '>' + escapeHtml(o.size) + '</option>';
            });
          } else {
            vals.forEach(function (v) {
              html += '<option value="' + escapeHtml(v) + '"' + (v === sel[oi] ? ' selected' : '') + '>' +
                escapeHtml(v) + '</option>';
            });
          }
          html += '</select>' + CARET_SVG + '</div>';
        });
        box.innerHTML = html;

        Array.prototype.forEach.call(box.querySelectorAll('.tabio-aifit__select'), function (s) {
          if (s.hasAttribute('data-size-select')) {
            s.addEventListener('change', function () { switchTo(s.value); });
            return;
          }
          s.addEventListener('change', function () {
            sel[parseInt(s.getAttribute('data-oi'), 10)] = s.value;
            syncImage();
            syncPrice();
            recordSelection(); // 選んだカラーを控える
          });
        });
      }

      // 選択中のバリアントに画像があればカードの画像を差し替える（カラー用・従来どおり）
      function syncImage() {
        if (!imgEl) return;
        var v = matched();
        var url = v && (v.featured_image ? v.featured_image.src : v.image);
        if (url) imgEl.src = url;
      }

      // カードの価格を「いまカートに入る バリアント」の価格に合わせる。
      // サイズ違いは別商品で価格が違うことがあり（実データ: 268組中46組）、
      // 表示とカート金額がずれないようにする。カラーで価格が違う商品にも同じく効く。
      // ★ /products/{handle}.js の price は cents 換算（×100）。円に戻してから
      //   既存の formatPrice に渡す（表示形式は従来のまま）。
      // ★ 価格を読めないときは何もしない（空欄・NaN にせず、元の表示のまま残す）。
      function syncPrice() {
        if (!priceEl) return;
        var v = matched();
        if (!v || v.price == null) return;
        var yen = Number(v.price) / 100;
        if (!isFinite(yen)) return;
        priceEl.textContent = formatPrice(yen);
      }

      // 商品リンクを切替先の商品に合わせる
      function syncLink() {
        if (!linkEl || !linkHref || !selectedHandle) return;
        linkEl.setAttribute('href',
          linkHref.replace(/\/products\/[^/?#]+/, '/products/' + encodeURIComponent(selectedHandle)));
      }

      // サイズ＝別商品なので、選ばれた商品に切り替える。
      // ★ 商品名・画像は変えない。「同じ商品のサイズ違い」に見せるため。
      //   変わるのはサイズ欄と、カート追加の対象・商品リンク・価格。
      //   価格だけは追従させる（サイズで値段が違う商品があり、表示とカート金額が
      //   ずれてしまうため）。
      function switchTo(handle) {
        if (!handle || handle === selectedHandle || switching) return;
        switching = true;
        cartBtn.disabled = true;
        var prevSel = sel.slice();
        fetchProduct(handle).then(function (p) {
          switching = false;
          cartBtn.disabled = false;
          if (p && readProduct(p, prevSel)) {
            selectedHandle = handle;
            syncLink();
            syncPrice();
          }
          // 取得できなかったときは選択を元のサイズに戻す（価格も元のまま）
          render();
          // 復元は一度きり。以後はお客様の操作がそのまま反映される。
          savedSel = null;
          // 取得に失敗したときも「いま画面に出ている状態」を控え直すので、
          // 保存内容と表示が食い違わない（無効な handle は自然に消える）。
          recordSelection();
        });
      }

      var cartBtn = document.createElement('button');
      cartBtn.type = 'button';
      cartBtn.className = 'tabio-aifit__cart';
      cartBtn.innerHTML = BAG_SVG + '<span>カートに追加</span>';
      cartBtn.addEventListener('click', function () {
        if (switching) return;
        var v = matched();
        if (!v) return;
        cartBtn.disabled = true;
        addToCart(v.id, cartBtn);
      });
      card.appendChild(cartBtn);

      readProduct(prod, null);
      // 自分のサイズ表記を API が読めなかったときは、商品自身のサイズ値で埋める
      // （選択肢のラベルが空にならないようにする）
      if (sizeIdx >= 0) {
        sizeChoices.forEach(function (o) {
          if (!o.size && o.handle === selectedHandle) o.size = sel[sizeIdx] || '';
        });
      }
      render();
      syncImage(); // 事前選択された色の画像を初期表示にも反映する
      syncPrice(); // 価格も同じく、初期表示から「いまカートに入る バリアント」に揃える
      syncLink();

      // どのサイズを選んだ状態にするか決める。
      //  1) 保存された選択（お客様が自分で選んだもの）を最優先で戻す
      //  2) 無ければ、推薦に使ったお客様のサイズ（API の customerSizeCm）に合う選択肢。
      //     会話でサイズを言っていない回（ログイン客の過去の診断記録で補った回）でも合わせるため。
      //  3) それも無ければ従来どおり、会話で言われたサイズを事前選択する
      // 保存された handle がいまのサイズ一覧に無い（廃番・取得不可など）ときは
      //  1) を捨てて 2)・3) に落とすので、既定の選択に戻るだけで壊れない。
      // customerSizeCm が無い（古い API・分からない回）ときは 3) だけ＝従来と同じ。
      var targetHandle = '';
      if (savedSel && savedSel.handle && isKnownSize(savedSel.handle)) {
        targetHandle = savedSel.handle;
      } else {
        var cm = customerSizeCmOf(customerSizeCm);
        var wanted = (cm !== null ? pickSizeChoiceByCms(sizeChoices, [cm]) : null) ||
          pickSizeChoice(sizeChoices, state.lastUserQuery);
        if (wanted) targetHandle = wanted.handle;
      }

      if (targetHandle && targetHandle !== selectedHandle) {
        // 表示だけ先に合わせる（取得を待つ間サイズが入れ替わって見えないように）。
        // カート追加は切替が終わるまで押せない（switchTo が無効化する）。
        var sizeSel = box.querySelector('[data-size-select]');
        if (sizeSel) sizeSel.value = targetHandle;
        switchTo(targetHandle); // 完了時に savedSel を捨てて控え直す
      } else {
        savedSel = null;
        recordSelection();
      }
    }

    // Shopify 標準カートに投入（同一オリジン）
    function addToCart(variantId, btn) {
      var label = btn.querySelector('span');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
        .then(function (res) {
          btn.disabled = false;
          if (res.ok) {
            // 先方指摘（2026-08-12）: 文言は「追加しました」・文字色は変えない。
            // .added は色を変えないが、状態を見分けたい箇所（テスト等）のために残す。
            btn.classList.add('added');
            if (label) label.textContent = '追加しました';
            refreshCartCount();
            setTimeout(function () {
              btn.classList.remove('added');
              if (label) label.textContent = 'カートに追加';
            }, 1800);
          } else {
            if (label) label.textContent = '追加できませんでした';
            setTimeout(function () { if (label) label.textContent = 'カートに追加'; }, 1800);
          }
        })
        .catch(function () {
          btn.disabled = false;
          if (label) label.textContent = 'カートに追加';
        });
    }

    // ── カート件数の表示 ──
    // 診断のヘッダー帯に出す件数（フローティングのみ）。
    // 全画面表示ではページのヘッダーが隠れて件数が見えないため、画面内にも出す。
    // ページ内設置ではページのヘッダーが見えているので作らない（二重表示しない）。
    var cartCountEl = null;
    var cartCountNumEl = null;
    if (isFloating && headerEl) {
      cartCountEl = document.createElement('span');
      cartCountEl.className = 'tabio-aifit__cart-count';
      cartCountEl.hidden = true;
      cartCountEl.innerHTML = BAG_SVG + '<span data-taf-cart-num></span>';
      cartCountNumEl = cartCountEl.querySelector('[data-taf-cart-num]');
      headerEl.appendChild(cartCountEl);
    }

    function showCartCount(count) {
      if (!cartCountEl || !cartCountNumEl) return;
      var n = Number(count);
      // 0件（と読めない値）のときは出さない
      if (!isFinite(n) || n <= 0) { cartCountEl.hidden = true; return; }
      cartCountNumEl.textContent = n;
      cartCountEl.setAttribute('aria-label', 'カートに' + n + '点');
      cartCountEl.hidden = false;
    }

    // 取得したカートを各所に反映する。
    // ★ タビオ様のテーマのヘッダーは <c-cart-count> というカスタム要素（assets/c_cart-count.js）で、
    //   クラス名では拾えない。テーマ自身も snippets/c_sku-cart-add.liquid で
    //   cart:refresh を投げて更新しているので、同じ作法に合わせる。
    //   従来のクラス名指定は他テーマ向けにそのまま残す（このテーマでは何にも当たらない）。
    function applyCartCount(cart) {
      if (!cart || cart.item_count == null) return;
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cart }
      }));
      ['.cart-count-bubble span', '[data-cart-count]', '.cart-count', '#CartCount', '.cart-count-bubble'].forEach(function (s) {
        Array.prototype.forEach.call(document.querySelectorAll(s), function (el) { el.textContent = cart.item_count; });
      });
      showCartCount(cart.item_count);
    }

    function refreshCartCount() {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(applyCartCount).catch(function () {});
    }

    // ── 入力欄 ──
    function setSendEnabled(enabled) {
      sendBtn.disabled = !enabled;
    }

    function sendFromInput() {
      var text = inputEl.value.trim();
      if (!text || state.sending) return;
      inputEl.value = '';
      inputEl.style.height = 'auto';
      handleUserTurn(text, text);
    }

    inputEl.addEventListener('input', function () {
      this.style.height = 'auto';
      this.style.height = Math.min(this.scrollHeight, 96) + 'px';
      if (!state.sending) setSendEnabled(this.value.trim().length > 0);
    });

    // Cmd+Enter(Mac)/Ctrl+Enter(Win) で送信、Enter 単体は改行（PoC 準拠）
    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        sendFromInput();
      }
    });

    sendBtn.addEventListener('click', sendFromInput);

    // 角丸の枠のどこを押しても入力できるようにする。
    // textarea は1行分の高さしかなく、枠の余白を押しても反応しなかったため。
    // textarea 自身と送信ボタンはそれぞれの既定動作に任せる
    var inputWrap = inputEl.closest('.tabio-aifit__input-wrap');
    if (inputWrap) {
      var isOwnTarget = function (target) {
        return target === inputEl || sendBtn.contains(target);
      };
      // 入力中に余白を押したとき、一度 blur してキーボードが閉じかけるのを防ぐ
      inputWrap.addEventListener('mousedown', function (e) {
        if (!isOwnTarget(e.target)) e.preventDefault();
      });
      inputWrap.addEventListener('click', function (e) {
        if (isOwnTarget(e.target)) return;
        inputEl.focus();
        var len = inputEl.value.length;
        try { inputEl.setSelectionRange(len, len); } catch (err) { /* noop */ }
      });
    }

    // 最初から診断をやり直す（先方要望 2026-09-08）。
    // ★ 2026-08-12 に削除した「戻る」ボタンとは別物。あちらはヘッダーにあり
    //   「前の画面へ戻る」と誤解されたため撤去した。こちらは結果の下に置き、
    //   文言で「やり直し」と明示しているので同じ誤解は起きない。
    // ★ フローティングでも閉じない（× と役割を分ける）。会話だけ捨てて TOP に戻す。
    if (restartBtn) {
      restartBtn.addEventListener('click', function () {
        resetToIntro();
        scrollToTop();
        inputEl.focus();
      });
    }

    // ★「戻る」ボタンは先方指摘（2026-08-12）で削除した。
    //   押すと会話がリセットされるのが「前の画面に戻る」と誤解されるため。
    //   会話のリセットは、ページ内設置の場合の × に残っている（下の closeBtn）。
    var closeBtn = root.querySelector('[data-close]');

    // 閉じる = フローティングならパネルを閉じる／ページ内設置なら TOP へ戻す
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        if (isFloating) {
          root.classList.add('is-closed');
          lockBackground(false);
          if (launcherBtn) launcherBtn.focus();
        } else {
          resetToIntro();
        }
      });
    }

    // 開いている間は背景（ページ本体）のスクロールを止める。
    // 全画面表示のため、裏側が動くと操作対象が分かりにくくなる。
    var savedScrollY = 0;
    function lockBackground(lock) {
      if (!isFloating) return;
      if (lock) {
        savedScrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = -savedScrollY + 'px';
        document.body.style.width = '100%';
      } else {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, savedScrollY);
      }
    }

    // フローティングは設定（初期チップ）の取得を「初めて開いたとき」まで遅らせる
    // （開かれない大半のページビューで API を呼ばないため）。開閉を繰り返しても1回だけ。
    var configRequested = false;

    // 開く処理は1か所にまとめ、ランチャーからも URL パラメータからも同じ経路を通す
    function openPanel() {
      preloadMascots(); // 開いた時点で読み始める（間に合わせるため）
      if (isFloating && !configRequested) {
        configRequested = true;
        loadConfig();
      }
      root.classList.remove('is-closed');
      lockBackground(true);
      // 開いた時点のカート件数をヘッダー帯に出す（前のページで入れた分も分かるように）
      refreshCartCount();
      setTimeout(function () { inputEl.focus(); }, 120);
    }

    if (launcherBtn) {
      launcherBtn.addEventListener('click', function () { openPanel(); });
    }

    if (isFloating) {
      document.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-tabio-aifit-open]');
        if (!trigger || !root.isConnected || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        openPanel();
      });
    }


    // フローティングは Esc でも閉じられるようにする
    if (isFloating) {
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !root.classList.contains('is-closed')) {
          root.classList.add('is-closed');
          lockBackground(false);
        }
      });
    }

    // ── 会話の続きが下にあるときの下スクロールボタン ──
    var scrollDownBtn = document.createElement('button');
    scrollDownBtn.type = 'button';
    scrollDownBtn.className = 'tabio-aifit__scroll-down';
    scrollDownBtn.setAttribute('aria-label', '最新のメッセージへ');
    scrollDownBtn.innerHTML = DOWN_SVG;
    scrollDownBtn.hidden = true;
    scrollDownBtn.addEventListener('click', function () {
      var s = scroller();
      s.scrollTo({ top: s.scrollHeight, behavior: 'smooth' });
    });
    if (inputAreaEl) inputAreaEl.appendChild(scrollDownBtn);

    function syncScrollDown() {
      // 下端から 40px 以上離れていて、かつスクロール可能なときだけ出す
      var s = scroller();
      var room = s.scrollHeight - s.clientHeight;
      scrollDownBtn.hidden = messagesEl.hidden || room < 40 || s.scrollTop >= room - 40;
    }
    // ★ 器が「パネル」にも「ページ」にもなり得るので、両方で拾う。
    //   scroll はバブルしないため、器になり得るものに個別に付ける必要がある。
    [window, root.querySelector('.tabio-aifit__panel'), messagesEl].forEach(function (t) {
      if (t) t.addEventListener('scroll', syncScrollDown, { passive: true });
    });
    // メッセージ追加のたびに判定する
    new MutationObserver(function () { setTimeout(syncScrollDown, 80); })
      .observe(messagesEl, { childList: true, subtree: true });

    // 保存済みの会話があれば画面を復元する（ページ遷移をまたいで続きから）
    function restoreIfAny() {
      var saved = loadState();
      if (!saved || !saved.messages.length) return;

      state.messages = saved.messages;
      state.lastUserQuery = saved.lastUserQuery || '';
      state.lastResult = saved.lastResult || null;
      state.selections = Array.isArray(saved.selections) ? saved.selections : [];
      // 話題の開始位置。保存が無い（この対応より前の保存）・範囲外なら 0＝会話全体。
      state.topicStart =
        (typeof saved.topicStart === 'number' && saved.topicStart > 0 && saved.topicStart < saved.messages.length)
          ? saved.topicStart
          : 0;

      enterConversation();

      // 会話を並べ直す（API は呼ばない）
      function replay(list) {
        list.forEach(function (m, i) {
          if (m.role === 'user') { addUserMessage(m.display || m.content); return; }
          // ★ 復元でも見た目を揃える。ただし結果の締めの一言（最後の1件で、
          //   商品カードのあとに来るもの）には添えない。
          var isClosing = i === list.length - 1 && m.content.indexOf('お手伝いできること') >= 0;
          addBotMessage(m.content, null, !isClosing);
        });
      }

      // ★ 以前の保存データに残っている「未回答の選択肢」（saved.pendingChoices）は
      //   読まない。追加質問の選択肢は出さない仕様にしたため（runNext のコメント参照）。

      // 直近の推薦結果が無ければ、会話をそのまま並べて終わり
      if (!state.lastResult || !(state.lastResult.products || []).length) {
        replay(saved.messages);
        return;
      }

      // ★ カード一式（導入文・カード・締めの一言）は state.messages に積んでいないので、
      //   会話の何番目にあったか（lastResult.at）を見て、その位置に挟み直す。
      //   これが無いと、結果が出たあとに会話を続けた場合、更新したときだけ
      //   古いカードが最新の質問より下に飛ぶ。
      //   位置を持たない古い保存データは末尾扱い＝従来どおりの並びにする。
      var at = (typeof state.lastResult.at === 'number' &&
                state.lastResult.at >= 0 &&
                state.lastResult.at <= saved.messages.length)
        ? state.lastResult.at
        : saved.messages.length;

      var products = state.lastResult.products;
      // 描き直しの途中で state が書き換わっても読み違えないよう、使う値は先に控える
      // （×で会話を捨てる・新しい結果が届く、が先に起きても古い組み合わせで描かない）。
      var savedSelections = state.selections.slice();
      var introText = state.lastResult.message;
      // 推薦に使ったお客様のサイズ。値の無い古い保存は null＝従来どおり会話から決める。
      var savedCustomerSizeCm = customerSizeCmOf(state.lastResult.customerSizeCm);

      replay(saved.messages.slice(0, at));
      prefetchVariants(products).then(function (variantMap) {
        if (introText) addBotMessage(introText);
        renderResult(products, variantMap, savedSelections, savedCustomerSizeCm);
        addBotMessage('他になにかお手伝いできることはありますか？');
        replay(saved.messages.slice(at));
      });
    }

    // 初期化
    setSendEnabled(false);
    // ページ内設置は最初から見えているので従来どおり初期化で取得する
    if (!isFloating) loadConfig();

    restoreIfAny();

    // URL に ?ai-fit=open が付いていれば、復元した会話を表示したまま開いた状態にする
    if (shouldAutoOpen(isFloating, window.location.search)) openPanel();
  }

  function initAll(ctx) {
    var roots = (ctx || document).querySelectorAll('[data-tabio-aifit]');
    for (var i = 0; i < roots.length; i++) initOne(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
